import MessageFlow from './MessageFlow';

const defaultProps = {
  /* Type of action to perform -- link, send, update, null */
  action: null,
  /* Whether this is an automated message or not */
  automated: false,
  /* The id for the conversation this message is a part of -- uuid string */
  conversationId: '',
  /* When this message was first created - string date ISO format */
  createdAt: new Date().toISOString(),
  /* The Date instance of this message's createdAt date */
  createdAtDate: new Date(),
  /* Text displayed next to date */
  detailText: '',
  /* The error message for this message -- used for local errors on send */
  error: '',
  /* Whether the message is hidden from the view */
  hidden: false,
  /* Temporary data held on the FE only when an image message fails to send */
  imageData: [],
  /* Images that are in the message */
  images: [],
  /* A stacked message will not show the timestamp or avatar */
  isStacked: false,
  /* Links that were parsed from the message content */
  links: [],
  /* Link thumbnail obtained from the website */
  linkThumbnail: null,
  /* Main message text */
  message: '',
  /**
   * The automated message flow for this message
   * used by the parent message for automated flows
  */
  messageFlow: null,
  /* The name of the parent message flow type */
  messageFlowName: '',
  /* The id for this message -- uuid string */
  messageId: '',
  /* Determines the type of subcomponent to display */
  messageType: 1,
  /* The message id for the parent message -- if this is an automated message */
  parentMessageId: null,
  /* Whether this message should animate in or not */
  shouldAnimate: false,
  /* The id for the space this message is attached to */
  spaceId: 0,
  /* Additional props to append to the subComponent (based on messageType) */
  subComponentProps: {},
  /* When this message was last updated - string date ISO format */
  updatedAt: new Date().toISOString(),
  /* The Date instance of this message's updatedAt date */
  updatedAtDate: new Date(),
  /* The user id for the message sender corresponds to MessageMember.id */
  userId: '0'
};

const convertInternal = ({ whMessage, spaceId, animateIn }) => {
  const messageFlow = whMessage.message_flow || whMessage.messageFlow;
  return {
    action: whMessage.action || defaultProps.action,
    automated: whMessage.automated || defaultProps.automated,
    conversationId: whMessage.conversationId || defaultProps.conversationId,
    createdAt: whMessage.createdAt || defaultProps.createdAt,
    createdAtDate: whMessage.createdAt ? new Date(whMessage.createdAt) : new Date(),
    detailText: whMessage.detailText || defaultProps.detailText,
    error: whMessage.error || defaultProps.error, // Not passed back from server
    hidden: whMessage.hidden || defaultProps.hidden,
    imageData: whMessage.imageData || defaultProps.imageData, // Not passed back from server
    images: whMessage.images || defaultProps.images,
    isStacked: whMessage.isStacked || defaultProps.isStacked,
    links: whMessage.links || defaultProps.links,
    linkThumbnail: whMessage.linkThumbnail || defaultProps.linkThumbnail,
    message: whMessage.message || defaultProps.message,
    messageFlow: (messageFlow && MessageFlow.createFromWarehouseResponse({
      whMessageFlow: messageFlow
    })) || defaultProps.messageFlow,
    messageFlowName: whMessage.messageFlowName || defaultProps.messageFlowName,
    messageId: whMessage.messageId || defaultProps.messageId,
    messageType: whMessage.messageType || defaultProps.messageType,
    parentMessageId: whMessage.parentMessageId || defaultProps.parentMessageId,
    shouldAnimate: animateIn || defaultProps.shouldAnimate,
    spaceId: spaceId || defaultProps.spaceId,
    subComponentProps: whMessage.subComponentProps || defaultProps.subComponentProps,
    updatedAt: whMessage.updatedAt || defaultProps.updatedAt,
    updatedAtDate: whMessage.updatedAt ? new Date(whMessage.updatedAt) : new Date(),
    userId: whMessage.userId ? whMessage.userId.toString() : defaultProps.userId
  };
};

class Message {
  /**
   * Creates a new Message based on the server response
   * @argument whMessage: raw message data from server
   * @argument spaceId: the space this message belongs to
   * @argument animateIn: Whether this message should animate into the thread or not
   */
  static createFromWarehouseResponse ({ whMessage, spaceId, animateIn }) {
    return new Message({
      ...convertInternal({ whMessage, spaceId, animateIn })
    });
  }

  /** Creates a new Message
   * @argument fields: The attributes to add to this instance
   */
  constructor (fields = defaultProps) {
    Object.assign(this, fields);
  }

  /** Generates a copy of the Message instance
   * @argument overrides: The attributes to add to the new instance
  */
  makeCopy (overrides) {
    return new Message({
      action: this.action,
      automated: this.automated,
      conversationId: this.conversationId,
      createdAt: this.createdAt,
      createdAtDate: new Date(this.createdAt),
      detailText: this.detailText,
      error: this.error,
      imageData: this.imageData,
      images: [...this.images],
      isStacked: this.isStacked,
      links: [...this.links],
      linkThumbnail: this.linkThumbnail,
      message: this.message,
      messageFlow: this.messageFlow ? this.messageFlow.makeCopy() : null,
      messageFlowName: this.messageFlowName,
      messageId: this.messageId,
      messageType: this.messageType,
      parentMessageId: this.parentMessageId,
      shouldAnimate: this.shouldAnimate,
      spaceId: this.spaceId,
      subComponentProps: { ...this.subComponentProps },
      updatedAt: this.updatedAt,
      updatedAtDate: new Date(this.updatedAt),
      userId: this.userId,
      ...overrides
    });
  }
}

export default Message;
